import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Save, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RECORTES_HTML_BASE } from '../../lib/recortes-html';

interface SlideEditorModalProps {
  slideUrls: string[];          // URLs absolutas de slides individuales
  initialIndex?: number;        // Slide con el que abrir (default 0)
  onClose: () => void;
}

type SaveStatus = 'idle' | 'saving' | 'ok' | 'error';

export function SlideEditorModal({ slideUrls, initialIndex = 0, onClose }: SlideEditorModalProps) {
  const [idx, setIdx] = useState(Math.max(0, Math.min(initialIndex, slideUrls.length - 1)));
  const [srcdoc, setSrcdoc] = useState('');
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveMsg, setSaveMsg] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const currentUrl = slideUrls[idx] ?? '';

  // Extraer path relativo desde la URL absoluta para el Storage
  function getStoragePath(url: string): string {
    // prod: https://...cubuslatam.com/api/guide/U1_C1/slides/slide-03.html → U1_C1/slides/slide-03.html
    // dev:  http://127.0.0.1:8765/U1_C1/slides/slide-03.html → U1_C1/slides/slide-03.html
    const base = RECORTES_HTML_BASE;
    // Quitar base y slash inicial
    if (url.startsWith(base)) {
      return url.slice(base.length).replace(/^\//, '');
    }
    // Fallback: último segmento de path (URL puede ser blob o diferente)
    try {
      const u = new URL(url);
      return u.pathname.replace(/^\/api\/guide\//, '').replace(/^\//, '');
    } catch {
      return '';
    }
  }

  // Cargar el slide en el iframe: fetch del HTML + inyectar deck-editor.js
  const loadSlide = useCallback(async (url: string) => {
    if (!url) return;
    setLoading(true);
    setSrcdoc('');
    setSaveStatus('idle');
    try {
      const res = await fetch(url + '?_t=' + Date.now(), { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      let html = await res.text();
      // <base> para que paths relativos del slide (img, css) resuelvan correctamente en srcdoc
      const baseHref = url.split('/').slice(0, -1).join('/') + '/';
      const baseTag = `<base href="${baseHref}">`;
      html = html.includes('<head>') ? html.replace('<head>', '<head>' + baseTag) : baseTag + html;
      // Inyectar deck-editor.js al final del body
      const editorScript = `<script src="/deck-editor.js" data-editor-script="1"><\/script>`;
      html = html.includes('</body>')
        ? html.replace('</body>', editorScript + '</body>')
        : html + editorScript;
      setSrcdoc(html);
    } catch (e: any) {
      setSrcdoc(`<html><body style="background:#050505;color:#ff6b6b;font-family:monospace;padding:40px">
        Error cargando slide: ${e?.message ?? 'desconocido'}</body></html>`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSlide(currentUrl); }, [currentUrl, loadSlide]);

  // Cuando el iframe notifica que el editor está listo → enviar el path del slide
  useEffect(() => {
    const path = getStoragePath(currentUrl);

    const handler = async (e: MessageEvent) => {
      if (!e.data) return;

      if (e.data.type === 'deck-editor-ready') {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'deck-slide-path', path },
          '*',
        );
      }

      if (e.data.type === 'deck-save') {
        const { html, path: msgPath } = e.data;
        const savePath = msgPath || path;
        if (!savePath) {
          iframeRef.current?.contentWindow?.postMessage({ type: 'deck-save-err', error: 'no path' }, '*');
          return;
        }
        setSaveStatus('saving');
        setSaveMsg('');
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token ?? '';
          const res = await fetch('/api/save-guide', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ path: savePath, html }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'error' }));
            throw new Error(err.error ?? `HTTP ${res.status}`);
          }
          setSaveStatus('ok');
          setSaveMsg(savePath.split('/').pop() ?? savePath);
          iframeRef.current?.contentWindow?.postMessage({ type: 'deck-save-ok' }, '*');
          setTimeout(() => setSaveStatus('idle'), 4000);
        } catch (err: any) {
          setSaveStatus('error');
          setSaveMsg(err?.message ?? 'error');
          iframeRef.current?.contentWindow?.postMessage({ type: 'deck-save-err', error: err?.message }, '*');
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [currentUrl]);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fileName = currentUrl.split('/').pop() ?? '';
  const total = slideUrls.length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Editor de slide"
      className="fixed inset-0 z-[300] bg-black/98 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-white/[0.08] bg-[#050505] flex-shrink-0">
        <span className="text-[10px] tracking-[2px] uppercase font-bold text-cubus-lime">
          // DECK-EDITOR
        </span>
        <span className="text-[10px] text-white/40 tracking-wide">
          slide {idx + 1} / {total} · {fileName}
        </span>
        <div className="flex items-center gap-1 ml-auto">
          {/* Navegación entre slides */}
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="p-1.5 rounded bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 disabled:opacity-30 text-white"
            title="Slide anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIdx(i => Math.min(total - 1, i + 1))}
            disabled={idx === total - 1}
            className="p-1.5 rounded bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 disabled:opacity-30 text-white"
            title="Slide siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Estado de guardado */}
          {saveStatus === 'saving' && (
            <span className="flex items-center gap-1.5 text-[11px] text-cubus-lime px-3">
              <Loader2 className="w-3 h-3 animate-spin" /> Guardando…
            </span>
          )}
          {saveStatus === 'ok' && (
            <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 px-3">
              <Save className="w-3 h-3" /> Guardado · {saveMsg}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-[11px] text-rose-400 px-3">Error: {saveMsg}</span>
          )}

          <button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 text-sm"
          >
            Cerrar <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Editor iframe */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#050505]">
            <Loader2 className="w-6 h-6 text-cubus-lime animate-spin" />
          </div>
        )}
        {srcdoc && (
          <iframe
            ref={iframeRef}
            key={currentUrl}
            srcDoc={srcdoc}
            title={`Editor · ${fileName}`}
            className="w-full h-full border-0 bg-[#050505]"
            sandbox="allow-scripts allow-same-origin allow-downloads allow-forms"
          />
        )}
      </div>

      {/* Thumbnails de slides */}
      {total > 1 && (
        <div className="flex-shrink-0 border-t border-white/[0.06] bg-[#080808] overflow-x-auto">
          <div className="flex gap-2 p-2 min-w-max">
            {slideUrls.map((url, i) => (
              <button
                key={url}
                onClick={() => setIdx(i)}
                className={`flex-shrink-0 w-28 h-16 rounded border text-[9px] font-mono overflow-hidden transition-all ${
                  i === idx
                    ? 'border-cubus-lime ring-1 ring-cubus-lime/40'
                    : 'border-white/[0.08] hover:border-white/30'
                }`}
                title={url.split('/').pop()}
              >
                <iframe
                  src={url}
                  className="w-full h-full border-0 pointer-events-none"
                  style={{ transform: 'scale(0.18)', transformOrigin: '0 0', width: '556%', height: '556%' }}
                  sandbox="allow-scripts allow-same-origin"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
